import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, hasDisplayValue } from "../utils/patientUtils";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  SummaryBadge,
  SummaryEmpty,
  SummaryLoading,
  SummaryTableWrap,
  summaryTableCell,
  summaryTableHead,
} from "./patientSummaryUi";
import type { SummaryTheme } from "./patientSummaryUi";
import {
  getLongTermBalancePayable,
  getSubTreatmentPaidAmount,
  isTreatmentItemPaid,
} from "../../manage/treatmenttable/components/treatments/treatmentItemUtils";
import { withAppointmentIdQuery } from "../../../utils/appointmentLinkContext";

type TreatmentDateSort = "desc" | "asc";
/** Primary status sort: selected status first, or off (`null` = date only). */
type TreatmentStatusSort = "planned" | "ongoing" | "Complete" | "long term" | null;

interface ExaminationTabProps {
  examinationData: any[];
  loading: boolean;
  theme?: SummaryTheme;
  /** Date sort: newest first (`desc`) or oldest first (`asc`). */
  dateSort?: TreatmentDateSort;
  /** When set, that status sorts first; date is secondary. */
  statusSort?: TreatmentStatusSort;
  /** Active visit from My Appointment / summary URL — kept on open so line saves update lastAppointmentId. */
  appointmentId?: string | null;
}

const STATUS_ORDER = ["planned", "ongoing", "Complete", "long term"] as const;

/** Treatment plans use `status: "long term"` and track progress in `planStatus`. */
const isTreatmentPlan = (entry: any) => entry?.status === "long term";

/** Only ongoing treatment plans count toward "Plan Total" — matches the treatment table's estimate rule. */
const isOngoingTreatmentPlan = (entry: any) =>
  isTreatmentPlan(entry) && (entry?.planStatus || "planned") === "ongoing";

const getEffectiveStatus = (entry: any): string => {
  if (isTreatmentPlan(entry)) {
    return entry?.planStatus || "planned";
  }
  return typeof entry?.status === "string" ? entry.status : "";
};

/** Key used for status sorting — treatment plans sort as `long term` when that mode is on. */
const getSortStatusKey = (entry: any, preferred: TreatmentStatusSort): string => {
  if (preferred === "long term") {
    return isTreatmentPlan(entry) ? "long term" : getEffectiveStatus(entry);
  }
  return getEffectiveStatus(entry);
};

const formatStatusLabel = (status: string) => {
  if (status === "planned") return "Planned";
  if (status === "ongoing") return "Ongoing";
  if (status === "Complete") return "Complete";
  if (status === "long term") return "Treatment Plan";
  return status;
};

const getStatusRank = (status: unknown, preferred: TreatmentStatusSort) => {
  if (!preferred || typeof status !== "string") return 99;
  if (status === preferred) return 0;
  const idx = STATUS_ORDER.indexOf(status as (typeof STATUS_ORDER)[number]);
  return idx === -1 ? 50 : idx + 1;
};

type TreatmentRow = {
  key: string;
  item: any;
  treatment: any;
  subTreatment?: any;
  isSub: boolean;
};

const formatTreatmentLabel = (entry: any) =>
  [entry?.treatment, entry?.type, entry?.subtype, entry?.stage].filter(hasDisplayValue).join(" · ");

const formatAmount = (entry: any) => {
  const raw = entry?.price ?? entry?.amount ?? entry?.total;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
};

const formatAmountLabel = (amount: number | null) =>
  amount == null ? "—" : `Rs ${amount.toLocaleString()}`;

const getTreatmentRowKey = (item: any, treatment: any, itemIndex: number, treatmentIndex: number) =>
  `${item?._id ?? itemIndex}-${treatment?._id ?? treatmentIndex}`;

const getUpdatedTime = (entry: any, fallback?: any) => {
  const value = entry?.updatedAt ?? entry?.createdAt ?? fallback?.updatedAt ?? fallback?.createdAt;
  return value ? new Date(value).getTime() : 0;
};

const formatEntryDate = (entry: any) => formatDate(entry?.updatedAt ?? entry?.createdAt);

const StatusBadge: React.FC<{
  status: string;
  theme: SummaryTheme;
}> = ({ status, theme }) => {
  const label = formatStatusLabel(status);
  const toneKey = status === "planned" || status === "ongoing" || status === "Complete" ? status : "other";

  return (
    <span
      className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        toneKey === "Complete"
          ? theme === "dark"
            ? "bg-emerald-500/20 text-emerald-300"
            : "bg-emerald-100 text-emerald-700"
          : toneKey === "ongoing"
            ? theme === "dark"
              ? "bg-amber-500/20 text-amber-300"
              : "bg-amber-100 text-amber-700"
            : toneKey === "planned"
              ? theme === "dark"
                ? "bg-sky-500/20 text-sky-300"
                : "bg-sky-100 text-sky-700"
              : theme === "dark"
                ? "bg-slate-700 text-slate-300"
                : "bg-slate-100 text-slate-600"
      }`}
    >
      {label}
    </span>
  );
};

const TreatmentPlanBadge: React.FC<{ theme: SummaryTheme }> = ({ theme }) => (
  <span
    className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
      theme === "dark"
        ? "bg-red-500/20 text-red-300"
        : "bg-red-100 text-red-700"
    }`}
  >
    Treatment Plan
  </span>
);

const BillStatusBadge: React.FC<{ paid: boolean; theme: SummaryTheme }> = ({ paid, theme }) => {
  const label = paid ? "Paid" : "Due";
  return (
    <span
      className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        paid
          ? theme === "dark"
            ? "bg-emerald-500/20 text-emerald-300"
            : "bg-emerald-100 text-emerald-700"
          : theme === "dark"
            ? "bg-red-500/20 text-red-300"
            : "bg-red-100 text-red-700"
      }`}
    >
      {label}
    </span>
  );
};

/**
 * Treatment plans ("long term") never carry a payment on the parent line —
 * they're billed one stage at a time via `subTreatment`. So instead of a
 * single Paid/Due badge, show what's actually been billed so far,
 * calculated straight from the plan's stages (same as the treatment table).
 */
const PlanBillBadges: React.FC<{
  paid: number;
  theme: SummaryTheme;
}> = ({ paid, theme }) => (
  <span
    className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
      theme === "dark"
        ? "bg-emerald-500/20 text-emerald-300"
        : "bg-emerald-100 text-emerald-700"
    }`}
  >
    Paid Rs {paid.toLocaleString()}
  </span>
);

const ExaminationTab: React.FC<ExaminationTabProps> = ({
  examinationData,
  loading,
  theme = "light",
  dateSort = "asc",
  statusSort = null,
  appointmentId = null,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const treatmentGroups = useMemo(() => {
    const groups = examinationData.flatMap((item: any, itemIndex: number) =>
      (item?.treatments ?? []).map((treatment: any, treatmentIndex: number) => ({
        rowKey: getTreatmentRowKey(item, treatment, itemIndex, treatmentIndex),
        item,
        treatment,
        subTreatments: Array.isArray(treatment?.subTreatment) ? [...treatment.subTreatment] : [],
      }))
    );

    const byDate = (aTime: number, bTime: number) =>
      dateSort === "asc" ? aTime - bTime : bTime - aTime;

    const byStatusThenDate = (aEntry: any, bEntry: any, aTime: number, bTime: number) => {
      if (statusSort) {
        const aRank = getStatusRank(getSortStatusKey(aEntry, statusSort), statusSort);
        const bRank = getStatusRank(getSortStatusKey(bEntry, statusSort), statusSort);
        if (aRank !== bRank) return aRank - bRank;
      }
      return byDate(aTime, bTime);
    };

    return groups
      .map((group) => ({
        ...group,
        subTreatments: group.subTreatments.sort((a: any, b: any) =>
          byStatusThenDate(a, b, getUpdatedTime(a), getUpdatedTime(b))
        ),
      }))
      .sort((a, b) =>
        byStatusThenDate(
          a.treatment,
          b.treatment,
          getUpdatedTime(a.treatment, a.item),
          getUpdatedTime(b.treatment, b.item)
        )
      );
  }, [examinationData, dateSort, statusSort]);

  const visibleRows = useMemo(() => {
    const rows: TreatmentRow[] = [];

    treatmentGroups.forEach(({ rowKey, item, treatment, subTreatments }) => {
      rows.push({
        key: rowKey,
        item,
        treatment,
        isSub: false,
      });

      if (expanded[rowKey] && subTreatments.length > 0) {
        subTreatments.forEach((subTreatment: any, subIndex: number) => {
          rows.push({
            key: `${rowKey}-sub-${subTreatment?._id ?? subIndex}`,
            item,
            treatment,
            subTreatment,
            isSub: true,
          });
        });
      }
    });

    return rows;
  }, [treatmentGroups, expanded]);

  const toggleExpanded = (rowKey: string) => {
    setExpanded((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  /**
   * Total Cost = sum of every parent treatment/plan amount in history.
   * "Plan Total" reflects only treatment plans that are actively ongoing —
   * their estimate and what's been paid across their stages — not regular
   * treatments, and not plans still just "planned" or already "Complete".
   */
  const billingTotals = useMemo(() => {
    let totalCost = 0;
    let total = 0;
    let paid = 0;

    treatmentGroups.forEach(({ treatment, subTreatments }) => {
      totalCost += formatAmount(treatment) ?? 0;
      if (!isOngoingTreatmentPlan(treatment)) return;
      total += formatAmount(treatment) ?? 0;
      paid += getSubTreatmentPaidAmount(subTreatments);
    });

    return { totalCost, total, paid, due: total - paid };
  }, [treatmentGroups]);

  if (loading) return <SummaryLoading theme={theme} />;

  if (treatmentGroups.length === 0) {
    return <SummaryEmpty message="No treatment history." theme={theme} />;
  }

  const head = summaryTableHead(theme);
  const cell = summaryTableCell(theme);

  return (
    <>
    <SummaryTableWrap>
      <table className="w-full min-w-[840px] border-separate border-spacing-0">
        <thead>
          <tr>
            <th className={`${head} w-10 rounded-tl-lg`} aria-label="Expand" />
            <th className={head}>Date</th>
            <th className={head}>Dentist</th>
            <th className={head}>Treatment</th>
            <th className={head}>Note</th>
            <th className={head}>Status</th>
            <th className={`${head} text-right`}>Amount</th>
            <th className={head}>Bill</th>
            <th className={`${head} text-center rounded-tr-lg`}>View</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ key, item, treatment, subTreatment, isSub }, index) => {
            const entry = isSub ? subTreatment : treatment;
            const dentist = entry?.didBy
              ? `${entry.didBy.firstName || ""} ${entry.didBy.lastName || ""}`.trim()
              : "";
            const name = formatTreatmentLabel(entry);
            const amount = formatAmount(entry);
            const isPlan = isTreatmentPlan(entry);
            const effectiveStatus = getEffectiveStatus(entry);
            const billPaid = isTreatmentItemPaid(entry);
            const isLast = index === visibleRows.length - 1;
            const subTreatments = Array.isArray(treatment?.subTreatment) ? treatment.subTreatment : [];
            const hasSubTreatments = !isSub && subTreatments.length > 0;
            const isExpanded = Boolean(expanded[key]);
            // Treatment plans bill per-stage, so the parent row's amount/bill
            // must be derived from its stages rather than its own paid flag.
            const isPlanRow = isPlan && !isSub;
            const planEstimate = amount ?? 0;
            const planPaid = isPlanRow ? getSubTreatmentPaidAmount(subTreatments) : 0;
            const planBalance = isPlanRow
              ? getLongTermBalancePayable(planEstimate, planPaid)
              : 0;
            // The row's own background/tint always mirrors what the Bill
            // column says — green once settled, red while still owed.
            const hasBillableAmount = isPlanRow ? planEstimate > 0 : amount != null;
            const rowIsPaid = isPlanRow ? planBalance <= 0 : billPaid;
            const rowToneClass = !hasBillableAmount
              ? theme === "dark"
                ? "border-slate-700/50 hover:bg-slate-800/40"
                : "border-slate-100 hover:bg-slate-50/80"
              : rowIsPaid
                ? theme === "dark"
                  ? "border-slate-700/50 bg-emerald-950/10 hover:bg-emerald-950/20"
                  : "border-slate-100 bg-emerald-50/40 hover:bg-emerald-50/70"
                : theme === "dark"
                  ? "border-slate-700/50 bg-red-950/10 hover:bg-red-950/20"
                  : "border-slate-100 bg-red-50/40 hover:bg-red-50/70";

            return (
              <tr key={key} className={`border-t ${rowToneClass}`}>
                <td className={`${cell} text-center ${isLast ? "rounded-bl-lg" : ""}`}>
                  {hasSubTreatments ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(key);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Hide sub treatments" : "Show sub treatments"}
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                        theme === "dark"
                          ? "text-amber-300 hover:bg-amber-500/20"
                          : "text-amber-700 hover:bg-amber-100"
                      }`}
                    >
                      {isExpanded ? (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      )}
                    </button>
                  ) : isSub ? (
                    <span className="inline-block w-8" aria-hidden />
                  ) : null}
                </td>
                <td className={cell}>{formatEntryDate(entry)}</td>
                <td className={cell}>{dentist || "—"}</td>
                <td
                  className={`${cell} ${isSub ? "pl-10" : ""} ${
                    isSub
                      ? theme === "dark"
                        ? "text-amber-200"
                        : "text-amber-900"
                      : `font-medium ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`
                  }`}
                >
                  {isSub ? (
                    `└ ${name || "—"}`
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="min-w-0">{name || "—"}</span>
                      {hasSubTreatments ? (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            theme === "dark"
                              ? "bg-amber-500/20 text-amber-300"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {subTreatments.length}
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td className={`${cell} max-w-[160px] truncate`}>
                  {hasDisplayValue(entry?.note) ? entry.note : "—"}
                </td>
                <td className={cell}>
                  {effectiveStatus || isPlan ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {isPlan ? <TreatmentPlanBadge theme={theme} /> : null}
                      {effectiveStatus ? (
                        <StatusBadge
                          status={effectiveStatus}
                          theme={theme}
                        />
                      ) : null}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td
                  className={`${cell} text-right tabular-nums font-semibold ${
                    !hasBillableAmount
                      ? theme === "dark"
                        ? "text-slate-500"
                        : "text-slate-400"
                      : theme === "dark"
                        ? "text-emerald-300"
                        : "text-emerald-700"
                  }`}
                >
                  {isPlanRow ? `Est. Rs ${planEstimate.toLocaleString()}` : formatAmountLabel(amount)}
                </td>
                <td className={cell}>
                  {isPlanRow ? (
                    <PlanBillBadges paid={planPaid} theme={theme} />
                  ) : (
                    <BillStatusBadge paid={billPaid} theme={theme} />
                  )}
                </td>
                <td className={`${cell} text-center ${isLast ? "rounded-br-lg" : ""}`}>
                  {!isSub ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item?._id) {
                          navigate(
                            withAppointmentIdQuery(
                              `/client/treatment-diagnose/add-treatment/treatment-table/${item._id}`,
                              appointmentId
                            )
                          );
                        }
                      }}
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                        theme === "dark"
                          ? "text-indigo-300 hover:bg-indigo-500/20"
                          : "text-indigo-600 hover:bg-indigo-50"
                      }`}
                      title="Open treatment"
                    >
                      <VisibilityIcon sx={{ fontSize: 17 }} />
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SummaryTableWrap>
    <div
      className={`mt-3 flex flex-wrap items-center justify-end gap-2 border-t pt-3 ${
        theme === "dark" ? "border-slate-700/80" : "border-slate-100"
      }`}
    >
      <SummaryBadge tone="neutral" theme={theme}>
        Total Cost Rs {billingTotals.totalCost.toLocaleString()}
      </SummaryBadge>
      <SummaryBadge tone="neutral" theme={theme}>
        Estimated Treatment Plan Cost Rs {billingTotals.total.toLocaleString()}
      </SummaryBadge>
      <SummaryBadge tone="info" theme={theme}>
        Paid Rs {billingTotals.paid.toLocaleString()}
      </SummaryBadge>
      {billingTotals.due > 0 ? (
        <SummaryBadge tone="danger" theme={theme}>
          Due Rs {billingTotals.due.toLocaleString()}
        </SummaryBadge>
      ) : billingTotals.due < 0 ? (
        <SummaryBadge tone="success" theme={theme}>
          Advance Rs {Math.abs(billingTotals.due).toLocaleString()}
        </SummaryBadge>
      ) : (
        <SummaryBadge tone="success" theme={theme}>
          Due Rs 0
        </SummaryBadge>
      )}
    </div>
    </>
  );
};

export default ExaminationTab;
